import { useContext, useEffect, useMemo, useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BookOpen, Plus, RotateCcw, Search, X } from 'lucide-react'
import { Alert, AlertDescription } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import { Field, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import { AdminSearchInput } from '@/components/admin/common/AdminSearchInput'
import { useTodayClasses } from '@/hooks/coaching/useTodayClasses'
import { useClassRoster, useTodayClassRosterSearch } from '@/hooks/coaching/useClassRoster'
import { useLearnerToday } from '@/hooks/coaching/useLearnerToday'
import { TodayClassList } from '@/components/admin/coaching/TodayClassList'
import { pickRecommendedTimeslotId } from '@/components/admin/coaching/todayClassUtils'
import { ClassRoster } from '@/components/admin/coaching/ClassRoster'
import { LearnerHistoryHeader } from '@/components/admin/learner-history/LearnerHistoryHeader'
import { SessionControls } from '@/components/admin/coaching/SessionControls'
import { LessonCompleteControl } from '@/components/admin/coaching/LessonCompleteControl'
import { LessonRefreshButton } from '@/components/admin/coaching/LessonRefreshButton'
import type { HandheldSelection } from '@/components/admin/coaching/handheld/handheldNavigation'
import { HandheldCoachingSelectionContext } from '@/components/admin/coaching/handheld/HandheldCoachingSelectionContext'
import { CoachingWorkArea } from '@/components/admin/coaching/CoachingWorkArea'
import { GrowthAlertButton } from '@/components/admin/coaching/GrowthAlertButton'
import { GrowthAlertPreviewDialog } from '@/components/admin/coaching/GrowthAlertPreviewDialog'
import { CreateUnitAssessmentModal } from '@/components/admin/coaching/CreateUnitAssessmentModal'
import { ContentFeePurchaseModal } from '@/components/admin/contentFee/ContentFeePurchaseModal'
import { TestCompletionControl } from '@/components/admin/coaching/test-completion/TestCompletionControl'
import { TestCompletionProgressBanner } from '@/components/admin/coaching/test-completion/TestCompletionProgressBanner'
import { useGrowthAlertPreview } from '@/hooks/coaching/useGrowthAlertPreview'
import type { LessonProcessDetailView, WorkbookMode } from '@/types/coaching'
import { requiresContentPayment } from '@/utils/contentFeeGate'

// detail 미로드 시 useMemo 의존성을 참조 안정화(매 렌더 새 배열 방지).
const EMPTY_LESSONS: LessonProcessDetailView[] = []

function parseSelectionId(value: string | null): number | null {
  if (value == null || !/^\d+$/.test(value)) return null
  const parsed = Number(value)
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null
}

function buildSelectionParams(
  current: URLSearchParams,
  timeslotId: number | null,
  learnerId: number | null,
): URLSearchParams {
  const next = new URLSearchParams(current)
  if (timeslotId == null) next.delete('timeslotId')
  else next.set('timeslotId', String(timeslotId))
  if (learnerId == null) next.delete('learnerId')
  else next.set('learnerId', String(learnerId))
  return next
}

/**
 * 오늘 수업(코칭 콘솔) — L·C 상하 스택 + 레슨 운영 스텝.
 *  L(상단 풀폭) = 클래스/원생 카드, C(하단 풀폭) = 원생 헤더/세션 + 메트릭 + 통합 작업영역,
 *  교과 정보와 이해도/워크북 운영은 레슨 채점 화면 안에서 제공한다.
 */
export default function CoachingPage() {
  const { t } = useTranslation('coaching')
  const [searchParams, setSearchParams] = useSearchParams()
  const requestedTimeslotId = parseSelectionId(searchParams.get('timeslotId'))
  const requestedLearnerId = parseSelectionId(searchParams.get('learnerId'))
  const [handheldSelection, setHandheldSelection] = useState<HandheldSelection | null>(null)
  const setShortcutSelection = useContext(HandheldCoachingSelectionContext)
  const [growthAlertOpen, setGrowthAlertOpen] = useState(false)
  const [rosterSearchOpen, setRosterSearchOpen] = useState(false)
  const [rosterSearchInput, setRosterSearchInput] = useState('')
  const [appliedRosterSearch, setAppliedRosterSearch] = useState<string | null>(null)
  const [unitAssessmentCreateOpen, setUnitAssessmentCreateOpen] = useState(false)
  const [contentFeeTargetUserId, setContentFeeTargetUserId] = useState<number | null>(null)
  const [workbookModeSelection, setWorkbookModeSelection] = useState<{
    learnerId: number
    lessonId: number
    mode: WorkbookMode
  } | null>(null)

  const classes = useTodayClasses()
  const visibleClasses = useMemo(
    () => (classes.data ?? []).filter((c) => c.studentCount > 0),
    [classes.data],
  )
  const selectedTimeslotId = visibleClasses.some((c) => c.timeslotId === requestedTimeslotId)
    ? requestedTimeslotId
    : pickRecommendedTimeslotId(visibleClasses)

  useEffect(() => {
    if (classes.isLoading) return
    const isCanonical = requestedTimeslotId === selectedTimeslotId
      && (selectedTimeslotId != null || requestedLearnerId == null)
    if (isCanonical) return
    setSearchParams(
      (current) => buildSelectionParams(current, selectedTimeslotId, null),
      { replace: true },
    )
  }, [classes.isLoading, requestedLearnerId, requestedTimeslotId, selectedTimeslotId, setSearchParams])

  const roster = useClassRoster(selectedTimeslotId)
  const rosterSearch = useTodayClassRosterSearch(appliedRosterSearch)
  const selectedLearnerId = requestedLearnerId != null
    && roster.data?.some((member) => member.userId === requestedLearnerId)
    ? requestedLearnerId
    : (roster.data?.[0]?.userId ?? null)

  const shortcutSelection = useMemo<HandheldSelection>(() => ({
    timeslotId: selectedTimeslotId,
    learnerId: selectedLearnerId,
    lessonId: handheldSelection?.learnerId === selectedLearnerId ? handheldSelection.lessonId : null,
    attemptId: handheldSelection?.learnerId === selectedLearnerId ? handheldSelection.attemptId : null,
    unitAssessmentId: handheldSelection?.learnerId === selectedLearnerId ? handheldSelection.unitAssessmentId : null,
    test: handheldSelection?.learnerId === selectedLearnerId ? handheldSelection.test : null,
  }), [selectedTimeslotId, selectedLearnerId, handheldSelection])

  useEffect(() => {
    setShortcutSelection?.(shortcutSelection)
  }, [setShortcutSelection, shortcutSelection])

  useEffect(() => () => setShortcutSelection?.(null), [setShortcutSelection])

  useEffect(() => {
    if (!roster.isSuccess || requestedLearnerId === selectedLearnerId) return
    setSearchParams(
      (current) => buildSelectionParams(current, selectedTimeslotId, selectedLearnerId),
      { replace: true },
    )
  }, [requestedLearnerId, roster.isSuccess, selectedLearnerId, selectedTimeslotId, setSearchParams])

  const detail = useLearnerToday(selectedLearnerId ?? 0)
  const selectedRosterItem = useMemo(
    () => roster.data?.find((m) => m.userId === selectedLearnerId),
    [roster.data, selectedLearnerId],
  )

  // 원생 전환 시 성장알림/단원평가 생성 닫기
  useEffect(() => {
    setGrowthAlertOpen(false)
    setUnitAssessmentCreateOpen(false)
  }, [selectedLearnerId])

  const lessons = detail.data?.lessons ?? EMPTY_LESSONS
  // 성장 알림 — 하원(checkOutAt) + 완료 레슨 ≥1 일 때만 활성. 다이얼로그가 열릴 때만 미리보기 조회.
  const completedLessonCount = lessons.filter((l) => l.status === 'completed').length
  const checkOutAt = detail.data?.sessionState.checkOutAt ?? null
  const growthAlert = useGrowthAlertPreview(selectedLearnerId ?? 0, undefined, growthAlertOpen)

  const resetRosterSearch = () => {
    setRosterSearchInput('')
    setAppliedRosterSearch(null)
  }

  const closeRosterSearch = () => {
    resetRosterSearch()
    setRosterSearchOpen(false)
  }

  const submitRosterSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const query = rosterSearchInput.trim()
    if (!query) return
    setRosterSearchInput(query)
    setAppliedRosterSearch(query)
  }

  if (classes.isLoading) return <div className="p-8" role="status" aria-live="polite">{t('dashboard.loading')}</div>
  if (classes.error) return <div className="p-8 text-red-600" role="alert">{t('dashboard.loadError')}</div>

  return (
    <div className="space-y-4">
      {/* L — 상단: 수업 목록 / 원생 목록 (영역 구분) */}
      <div className="rounded-lg border border-gray-200 bg-white">
        <section className="border-b border-gray-100 p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('classList.sectionTitle')}
            </h2>
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              {!rosterSearchOpen ? (
                <Button type="button" size="sm" variant="outline" onClick={() => setRosterSearchOpen(true)}>
                  <Search data-icon="inline-start" />
                  {t('roster.openSearch')}
                </Button>
              ) : null}
            </div>
          </div>
          {rosterSearchOpen ? <form className="mb-3" onSubmit={submitRosterSearch}>
            <FieldGroup className="gap-2">
              <Field orientation="responsive">
                <FieldLabel htmlFor="today-roster-search" className="sr-only">
                  {t('roster.searchLabel')}
                </FieldLabel>
                <AdminSearchInput
                  id="today-roster-search"
                  value={rosterSearchInput}
                  maxLength={100}
                  onValueChange={setRosterSearchInput}
                  onClear={() => setRosterSearchInput('')}
                  clearLabel={t('roster.searchClear')}
                  placeholder={t('roster.searchPlaceholder')}
                  containerClassName="@md/field-group:max-w-md"
                />
                <div className="flex gap-2">
                  <Button type="submit" disabled={!rosterSearchInput.trim()}>
                    <Search data-icon="inline-start" />
                    {t('roster.search')}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={appliedRosterSearch == null && rosterSearchInput.length === 0}
                    onClick={resetRosterSearch}
                  >
                    <RotateCcw data-icon="inline-start" />
                    {t('roster.resetSearch')}
                  </Button>
                  <Button
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    aria-label={t('roster.closeSearch')}
                    onClick={closeRosterSearch}
                  >
                    <X />
                  </Button>
                </div>
              </Field>
            </FieldGroup>
          </form> : null}
          <TodayClassList
            classes={visibleClasses}
            selectedId={selectedTimeslotId}
            onSelect={(id) => {
              closeRosterSearch()
              setSearchParams((current) => buildSelectionParams(current, id, null))
            }}
          />
        </section>
        <section className="p-3">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                {appliedRosterSearch == null ? t('roster.sectionTitle') : t('roster.searchResultTitle')}
              </h2>
              {appliedRosterSearch != null && rosterSearch.isSuccess ? (
                <span className="rounded-full bg-primary-50 px-2 py-0.5 text-xs font-semibold text-primary-700" aria-live="polite">
                  {t('roster.searchResultCount', { count: rosterSearch.data.length })}
                </span>
              ) : null}
            </div>
          </div>
          {appliedRosterSearch != null && rosterSearch.error ? (
            <Alert variant="destructive">
              <AlertDescription className="flex items-center justify-between gap-3">
                <span>{t('roster.searchError')}</span>
                <Button type="button" size="sm" variant="outline" onClick={() => rosterSearch.refetch()}>
                  {t('roster.retry')}
                </Button>
              </AlertDescription>
            </Alert>
          ) : appliedRosterSearch != null && rosterSearch.isSuccess && rosterSearch.data.length === 0 ? (
            <Alert>
              <AlertDescription className="flex items-center justify-between gap-3">
                <span>{t('roster.noSearchResults')}</span>
                <Button type="button" size="sm" variant="outline" onClick={resetRosterSearch}>
                  {t('roster.resetSearch')}
                </Button>
              </AlertDescription>
            </Alert>
          ) : (
            <ClassRoster
              items={appliedRosterSearch != null ? (rosterSearch.data ?? []) : (roster.data ?? [])}
              selectedLearnerId={selectedLearnerId}
              onSelect={(id, resultTimeslotId) => setSearchParams(
                (current) => buildSelectionParams(current, resultTimeslotId ?? selectedTimeslotId, id),
              )}
              onContentPayment={setContentFeeTargetUserId}
              isLoading={appliedRosterSearch != null
                ? rosterSearch.isLoading
                : selectedTimeslotId != null && roster.isLoading}
            />
          )}
        </section>
      </div>

      <TestCompletionProgressBanner />

      {/* C — 작업영역(전체 폭) */}
      {selectedLearnerId == null ? (
        <div className="rounded-lg border border-dashed border-gray-200 p-12 text-center text-sm text-gray-500" role="status" aria-live="polite">
          {t('pane.selectLearner')}
        </div>
      ) : detail.error ? (
        <div className="p-8 text-red-600" role="alert">{t('dashboard.loadError')}</div>
      ) : detail.isLoading || !detail.data ? (
        <div className="p-8" role="status" aria-live="polite">{t('dashboard.loading')}</div>
      ) : (
        <>
          <LearnerHistoryHeader
            learner={detail.data.learner}
            summary={detail.data.summary}
            rosterItem={selectedRosterItem}
            detailHref={`/admin/students/${selectedLearnerId}`}
            actions={
              <div className="flex flex-wrap items-center justify-end gap-2">
                <TestCompletionControl learnerId={selectedLearnerId} />
                {(selectedRosterItem?.textbookAlertCount ?? 0) > 0 &&
                !(
                  selectedRosterItem?.attendanceStatus !== 'LEAVE' &&
                  requiresContentPayment(selectedRosterItem?.contentFeeGate, selectedRosterItem?.contentFeeNotice)
                ) ? (
                  <Link
                    to={`/admin/students/${selectedLearnerId}?tab=textbook`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-800 transition-colors hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-500"
                  >
                    <BookOpen className="size-4" aria-hidden="true" />
                    {t('roster.textbookAlert')}
                  </Link>
                ) : null}
                <SessionControls
                  learnerId={selectedLearnerId}
                  sessionState={detail.data.sessionState}
                  extraActions={(
                    <button
                      type="button"
                      onClick={() => setUnitAssessmentCreateOpen(true)}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
                    >
                      <Plus size={16} />
                      {t('unitAssessment.title')}
                    </button>
                  )}
                />
                <GrowthAlertButton
                  checkOutAt={checkOutAt}
                  completedLessonCount={completedLessonCount}
                  onClick={() => setGrowthAlertOpen(true)}
                />
              </div>
            }
          />

          <CoachingWorkArea
            learnerId={selectedLearnerId}
            timeslotId={selectedTimeslotId}
            todayLessons={detail.data.lessons}
            onHandheldSelectionChange={setHandheldSelection}
            workbookModeSelection={workbookModeSelection}
            onWorkbookModeChange={(lessonId, mode) => {
              setWorkbookModeSelection({ learnerId: selectedLearnerId, lessonId, mode })
            }}
            renderLessonActions={(lesson) => (
              <>
                <LessonRefreshButton learnerId={selectedLearnerId} lessonId={lesson.lessonId} />
                <LessonCompleteControl
                  learnerId={selectedLearnerId}
                  lesson={lesson}
                  attendanceOpen={detail.data.sessionState.attendanceOpen}
                  workbookMode={
                    workbookModeSelection?.learnerId === selectedLearnerId &&
                    workbookModeSelection.lessonId === lesson.lessonId
                      ? workbookModeSelection.mode
                      : 'IMMEDIATE'
                  }
                />
              </>
            )}
          />

          {growthAlertOpen ? (
            <GrowthAlertPreviewDialog
              preview={growthAlert.data}
              isLoading={growthAlert.isLoading}
              isError={growthAlert.isError}
              onClose={() => setGrowthAlertOpen(false)}
            />
          ) : null}

          {unitAssessmentCreateOpen ? (
            <CreateUnitAssessmentModal
              learnerId={selectedLearnerId}
              onClose={() => setUnitAssessmentCreateOpen(false)}
              onCreated={() => setUnitAssessmentCreateOpen(false)}
            />
          ) : null}
        </>
      )}
      {contentFeeTargetUserId != null ? (
        <ContentFeePurchaseModal
          userId={contentFeeTargetUserId}
          open
          onClose={() => setContentFeeTargetUserId(null)}
        />
      ) : null}
    </div>
  )
}
